import React, { useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/userAuth/UserAuthState';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  BsSpeedometer2,
  BsFileEarmarkPlus,
  BsPatchCheck,
  BsGear,
  BsSliders,
  BsBoxArrowRight,
  BsList,
  BsReceipt,
  BsCashStack,
} from 'react-icons/bs';

// ─── Nav items config ─────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { to: '/dashboard',                 label: 'Dashboard',          icon: BsSpeedometer2 },
  { to: '/all-certifications',        label: 'All Certifications', icon: BsPatchCheck   },
  { to: '/create-certificate',        label: 'Create Certificate', icon: BsFileEarmarkPlus },
  { to: '/all-invoices',              label: 'All Invoices',       icon: BsReceipt      },
  { to: '/create-invoice',            label: 'Create Invoice',     icon: BsCashStack    },
  { to: '/certificate-configuration', label: 'Configuration',      icon: BsSliders      },
  { to: '/settings',                  label: 'Settings',           icon: BsGear         },
];

// ─── DashboardLayout ──────────────────────────────────────────────────────────
const DashboardLayout = ({ children, pageTitle }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuth();
  const offcanvasRef = useRef(null);

  const firstName = user?.user?.first_name || 'Admin';
  const lastName  = user?.user?.last_name  || '';
  const initials  = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();

  // Close offcanvas programmatically after nav click on mobile
  const closeMobileSidebar = () => {
    if (offcanvasRef.current) {
      const bsOffcanvas = window.bootstrap?.Offcanvas?.getInstance(offcanvasRef.current);
      if (bsOffcanvas) bsOffcanvas.hide();
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    closeMobileSidebar();
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // ─── Sidebar content (shared between desktop + mobile) ───────────────────
  const SidebarContent = ({ onNavClick }) => (
    <div className="db-sidebar__body">
      {/* Brand */}
      <div className="db-sidebar__brand">
        <img
          src="/assets/logo/color_logo.png"
          alt="TUV PAK"
          className="db-sidebar__logo"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <span className="db-sidebar__brand-name">TUV PAK</span>
      </div>

      {/* User info */}
      <div className="db-sidebar__user">
        <div className="db-sidebar__avatar">{initials}</div>
        <div className="db-sidebar__user-info">
          <span className="db-sidebar__user-name">{firstName} {lastName}</span>
          <span className="db-sidebar__user-role">Administrator</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="db-sidebar__nav">
        <p className="db-sidebar__nav-label">Menu</p>
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className={`db-sidebar__link${isActive(to) ? ' db-sidebar__link--active' : ''}`}
            onClick={onNavClick}
          >
            <Icon size={18} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="db-sidebar__footer">
        <button className="db-sidebar__logout" onClick={handleLogout}>
          <BsBoxArrowRight size={18} />
          <span>Sign Out</span>
        </button>
        <p className="db-sidebar__copy">© {new Date().getFullYear()} TUV PAK</p>
      </div>
    </div>
  );

  return (
    <div className="db-layout">

      {/* ── Desktop sidebar ────────────────────────────────────── */}
      <aside className="db-sidebar db-sidebar--desktop">
        <SidebarContent onNavClick={() => {}} />
      </aside>

      {/* ── Mobile offcanvas sidebar ───────────────────────────── */}
      <div
        className="offcanvas offcanvas-start db-offcanvas"
        tabIndex="-1"
        id="mobileSidebar"
        aria-labelledby="mobileSidebarLabel"
        ref={offcanvasRef}
      >
        <div className="offcanvas-header db-offcanvas__header">
          <span id="mobileSidebarLabel" className="db-sidebar__brand-name">TUV PAK</span>
          <button
            type="button"
            className="btn-close btn-close-white"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>
        <div className="offcanvas-body p-0">
          <SidebarContent onNavClick={closeMobileSidebar} />
        </div>
      </div>

      {/* ── Main content area ──────────────────────────────────── */}
      <div className="db-main">

        {/* Top bar */}
        <header className="db-topbar">
          {/* Mobile hamburger */}
          <button
            className="db-topbar__hamburger d-md-none"
            type="button"
            data-bs-toggle="offcanvas"
            data-bs-target="#mobileSidebar"
            aria-controls="mobileSidebar"
            aria-label="Open menu"
          >
            <BsList size={24} />
          </button>

          {/* Page title */}
          <h1 className="db-topbar__title">
            {pageTitle || NAV_ITEMS.find(n => isActive(n.to))?.label || 'Dashboard'}
          </h1>

          {/* Right: user chip */}
          <div className="db-topbar__right">
            <div className="db-topbar__user">
              <div className="db-topbar__avatar">{initials}</div>
              <span className="db-topbar__user-name d-none d-sm-inline">
                {firstName} {lastName}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="db-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
